import React, { createContext, useContext, useReducer, useCallback } from 'react'
import cloneDeep from 'lodash/cloneDeep'

// Create Context
const AppContext = createContext()

// Initial State
const initialState = {
  // Bugfix List
  bugfixList: [],
  fetchBugfixListInProgress: false,
  fetchBugfixListHasErrored: false,
  
  // Bugfix Project
  bugfixProject: {},
  addBugfixProjectInProgress: false,
  addBugfixProjectHasErrored: false,
  
  // Repositories
  repositories: [],
  repoSuggestions: [],
  fetchRepositoriesInProgress: false,
  fetchRepositoriesHasErrored: false,
  
  // Repo Strings
  repoStrings: {},
  fetchRepoStringsInProgress: false,
  fetchRepoStringsHasErrored: false,
  
  // Repo Strings Suggestions
  repoStringsSuggestions: [],
  
  // String Values
  stringValues: []
}

// Action Types
export const actionTypes = {
  // Bugfix List
  FETCH_BUGFIX_LIST_START: 'FETCH_BUGFIX_LIST_START',
  FETCH_BUGFIX_LIST_SUCCESS: 'FETCH_BUGFIX_LIST_SUCCESS',
  FETCH_BUGFIX_LIST_ERROR: 'FETCH_BUGFIX_LIST_ERROR',
  TOGGLE_BUG: 'TOGGLE_BUG',
  
  // Bugfix Project
  ADD_BUGFIX_PROJECT_START: 'ADD_BUGFIX_PROJECT_START',
  ADD_BUGFIX_PROJECT_SUCCESS: 'ADD_BUGFIX_PROJECT_SUCCESS',
  ADD_BUGFIX_PROJECT_ERROR: 'ADD_BUGFIX_PROJECT_ERROR',
  RESET_ADD_BUGFIX_PROJECT: 'RESET_ADD_BUGFIX_PROJECT',
  
  // Repositories
  FETCH_REPOSITORIES_START: 'FETCH_REPOSITORIES_START',
  FETCH_REPOSITORIES_SUCCESS: 'FETCH_REPOSITORIES_SUCCESS',
  FETCH_REPOSITORIES_ERROR: 'FETCH_REPOSITORIES_ERROR',
  
  // Repo Strings
  FETCH_REPOSTRINGS_START: 'FETCH_REPOSTRINGS_START',
  FETCH_REPOSTRINGS_SUCCESS: 'FETCH_REPOSTRINGS_SUCCESS',
  FETCH_REPOSTRINGS_ERROR: 'FETCH_REPOSTRINGS_ERROR',
  
  // Repo Strings Suggestions
  ADD_REPO_STRING_SUGGESTION: 'ADD_REPO_STRING_SUGGESTION',
  REMOVE_REPO_STRING_SUGGESTION: 'REMOVE_REPO_STRING_SUGGESTION',
  RESET_REPO_STRINGS_SUGGESTIONS: 'RESET_REPO_STRINGS_SUGGESTIONS',
  
  // String Values
  ADD_STRING_VALUE: 'ADD_STRING_VALUE',
  REMOVE_BUG_FIELD_STRING_VALUES: 'REMOVE_BUG_FIELD_STRING_VALUES',
  REMOVE_STRING_FIELD_STRING_VALUES: 'REMOVE_STRING_FIELD_STRING_VALUES',
  ADD_BUG_FIELD_STRING_VALUES: 'ADD_BUG_FIELD_STRING_VALUES',
  ADD_STRING_FIELD_STRING_VALUES: 'ADD_STRING_FIELD_STRING_VALUES',
  FETCH_STRING_VALUE_START: 'FETCH_STRING_VALUE_START',
  FETCH_STRING_VALUE_SUCCESS: 'FETCH_STRING_VALUE_SUCCESS',
  FETCH_STRING_VALUE_ERROR: 'FETCH_STRING_VALUE_ERROR'
}

// Reducer
function appReducer(state, action) {
  switch (action.type) {
    // Bugfix List
    case actionTypes.FETCH_BUGFIX_LIST_START:
      return {
        ...state,
        fetchBugfixListInProgress: true,
        fetchBugfixListHasErrored: false
      }
    
    case actionTypes.FETCH_BUGFIX_LIST_SUCCESS:
      return {
        ...state,
        fetchBugfixListInProgress: false,
        bugfixList: action.payload.map((project) => {
          project.bugs.forEach(bug => (bug.expanded = false))
          if (!project.awsStatus) project.awsStatus = 'No Status'
          return project
        })
      }
    
    case actionTypes.FETCH_BUGFIX_LIST_ERROR:
      return {
        ...state,
        fetchBugfixListInProgress: false,
        fetchBugfixListHasErrored: true
      }
    
    case actionTypes.TOGGLE_BUG: {
      const { project, bug } = action
      const newBugfixList = cloneDeep(state.bugfixList)
      newBugfixList[project].bugs[bug].expanded = !newBugfixList[project].bugs[bug].expanded
      return {
        ...state,
        bugfixList: newBugfixList
      }
    }
    
    // Bugfix Project
    case actionTypes.ADD_BUGFIX_PROJECT_START:
      return {
        ...state,
        addBugfixProjectInProgress: true,
        addBugfixProjectHasErrored: false
      }
    
    case actionTypes.ADD_BUGFIX_PROJECT_SUCCESS:
      return {
        ...state,
        addBugfixProjectInProgress: false,
        bugfixProject: action.payload
      }
    
    case actionTypes.ADD_BUGFIX_PROJECT_ERROR:
      return {
        ...state,
        addBugfixProjectInProgress: false,
        addBugfixProjectHasErrored: true
      }
    
    case actionTypes.RESET_ADD_BUGFIX_PROJECT:
      return {
        ...state,
        addBugfixProjectInProgress: false,
        addBugfixProjectHasErrored: false,
        bugfixProject: {}
      }
    
    // Repositories
    case actionTypes.FETCH_REPOSITORIES_START:
      return {
        ...state,
        fetchRepositoriesInProgress: true,
        fetchRepositoriesHasErrored: false
      }
    
    case actionTypes.FETCH_REPOSITORIES_SUCCESS: {
      const repositories = action.payload
      const repoSuggestions = repositories.map((repo) => {
        return repo.description ? `${repo.name} - ${repo.description}` : repo.name
      })
      return {
        ...state,
        fetchRepositoriesInProgress: false,
        repositories,
        repoSuggestions
      }
    }
    
    case actionTypes.FETCH_REPOSITORIES_ERROR:
      return {
        ...state,
        fetchRepositoriesInProgress: false,
        fetchRepositoriesHasErrored: true
      }
    
    // Repo Strings
    case actionTypes.FETCH_REPOSTRINGS_START:
      return {
        ...state,
        fetchRepoStringsInProgress: true,
        fetchRepoStringsHasErrored: false
      }
    
    case actionTypes.FETCH_REPOSTRINGS_SUCCESS:
      return {
        ...state,
        fetchRepoStringsInProgress: false,
        repoStrings: {
          ...state.repoStrings,
          [action.repoName]: action.payload
        }
      }
    
    case actionTypes.FETCH_REPOSTRINGS_ERROR:
      return {
        ...state,
        fetchRepoStringsInProgress: false,
        fetchRepoStringsHasErrored: true
      }
    
    // Repo Strings Suggestions
    case actionTypes.ADD_REPO_STRING_SUGGESTION: {
      const { repository, bugIndex, stringIndex } = action
      const newSuggestions = cloneDeep(state.repoStringsSuggestions)
      if (!newSuggestions[bugIndex]) {
        newSuggestions[bugIndex] = []
      }
      if (!newSuggestions[bugIndex][stringIndex]) {
        newSuggestions[bugIndex][stringIndex] = []
      }
      newSuggestions[bugIndex][stringIndex] = repository
      return {
        ...state,
        repoStringsSuggestions: newSuggestions
      }
    }
    
    case actionTypes.REMOVE_REPO_STRING_SUGGESTION: {
      const { bugIndex, stringIndex } = action
      const newSuggestions = cloneDeep(state.repoStringsSuggestions)
      if (newSuggestions[bugIndex] && newSuggestions[bugIndex][stringIndex]) {
        newSuggestions[bugIndex][stringIndex] = []
      }
      return {
        ...state,
        repoStringsSuggestions: newSuggestions
      }
    }
    
    case actionTypes.RESET_REPO_STRINGS_SUGGESTIONS:
      return {
        ...state,
        repoStringsSuggestions: []
      }
    
    // String Values
    case actionTypes.ADD_STRING_VALUE: {
      const { bugIndex, stringIndex, value } = action
      const newStringValues = cloneDeep(state.stringValues)
      if (!newStringValues[bugIndex]) {
        newStringValues[bugIndex] = []
      }
      if (!newStringValues[bugIndex][stringIndex]) {
        newStringValues[bugIndex][stringIndex] = []
      }
      newStringValues[bugIndex][stringIndex] = value
      return {
        ...state,
        stringValues: newStringValues
      }
    }
    
    case actionTypes.ADD_BUG_FIELD_STRING_VALUES:
      return {
        ...state,
        stringValues: [...state.stringValues, []]
      }
    
    case actionTypes.ADD_STRING_FIELD_STRING_VALUES: {
      const newStringValues = cloneDeep(state.stringValues)
      if (!newStringValues[action.bugIndex]) {
        newStringValues[action.bugIndex] = []
      }
      newStringValues[action.bugIndex].push([])
      return {
        ...state,
        stringValues: newStringValues
      }
    }
    
    case actionTypes.REMOVE_BUG_FIELD_STRING_VALUES: {
      const newStringValues = cloneDeep(state.stringValues)
      newStringValues.splice(action.bugIndex, 1)
      return {
        ...state,
        stringValues: newStringValues
      }
    }
    
    case actionTypes.REMOVE_STRING_FIELD_STRING_VALUES: {
      const newStringValues = cloneDeep(state.stringValues)
      if (newStringValues[action.bugIndex]) {
        newStringValues[action.bugIndex].splice(action.stringIndex, 1)
      }
      return {
        ...state,
        stringValues: newStringValues
      }
    }
    
    case actionTypes.FETCH_STRING_VALUE_SUCCESS: {
      const { resource, bugIndex, stringIndex } = action
      const newStringValues = cloneDeep(state.stringValues)
      if (!newStringValues[bugIndex]) {
        newStringValues[bugIndex] = []
      }
      if (!newStringValues[bugIndex][stringIndex]) {
        newStringValues[bugIndex][stringIndex] = []
      }
      newStringValues[bugIndex][stringIndex] = resource
      return {
        ...state,
        stringValues: newStringValues
      }
    }
    
    default:
      return state
  }
}

// Provider Component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState)
  
  const value = {
    state,
    dispatch
  }
  
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

// Hook to use the context
export function useAppContext() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider')
  }
  return context
}

// Export for backward compatibility
export default AppContext
